import type { ReactNode } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { PayloadField, PayloadForm } from '../model/payload-form';

/**
 * The inputs a published schema describes.
 *
 * Every field here came from `GET /v1/policies/{code}/schema` — the schema the policy's PIN
 * resolved to, not the newest one (architecture §B.5). Nothing is hard-coded, which is what makes
 * it correct for a policy written against a schema this build has never seen.
 *
 * Field names are shown as the schema states them. Translating them would need a key per field of
 * every product ever published, and getting one wrong would relabel a field an operator is
 * entering data into — a worse failure than an untranslated identifier.
 */
export function PayloadFields({
  form,
  values,
  onChange,
  errors,
  disabled,
}: {
  readonly form: PayloadForm;
  readonly values: Readonly<Record<string, string>>;
  readonly onChange: (name: string, value: string) => void;
  readonly errors: Readonly<Record<string, string>>;
  readonly disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation('insurance');

  return (
    <Stack spacing={2}>
      {form.groups.map((group) => (
        <Stack key={group} spacing={1.5} component="section" aria-label={group === '' ? undefined : group}>
          {group !== '' && (
            // The insured object or question group, as the schema names it (C-26): a payload
            // is one sub-document per object, and the form says which one it is filling.
            <Typography variant="subtitle2" component="h3" sx={{ fontFamily: 'monospace' }}>
              {group}
            </Typography>
          )}
          {form.fields
            .filter((field) => field.group === group)
            .map((field) => (
              <PayloadInput
                key={field.name}
                field={field}
                value={values[field.name] ?? ''}
                onChange={(value) => {
                  onChange(field.name, value);
                }}
                error={errors[field.name] ?? ''}
                disabled={disabled === true}
              />
            ))}
        </Stack>
      ))}
      {form.unsupported.length > 0 && (
        // Named rather than swallowed: a property this reader cannot render is a gap in the form,
        // and an operator who cannot see it would submit a payload missing a field.
        <TextField
          disabled
          fullWidth
          label={t('policies.endorse.unsupported')}
          value={form.unsupported.join(', ')}
          helperText={t('policies.endorse.unsupportedHint')}
        />
      )}
    </Stack>
  );
}

function PayloadInput({
  field,
  value,
  onChange,
  error,
  disabled,
}: {
  readonly field: PayloadField;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error: string;
  readonly disabled: boolean;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const common = {
    label: field.label,
    value,
    required: field.required,
    disabled,
    fullWidth: true,
    error: error !== '',
    helperText: error === '' ? undefined : error,
    onChange: (event: { target: { value: string } }) => {
      onChange(event.target.value);
    },
  };

  if (field.kind === 'enum') {
    return (
      <TextField {...common} select>
        {(field.options ?? []).map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (field.kind === 'boolean') {
    return (
      <TextField {...common} select>
        <MenuItem value="true">{t('policies.endorse.yes')}</MenuItem>
        <MenuItem value="false">{t('policies.endorse.no')}</MenuItem>
      </TextField>
    );
  }

  return (
    <TextField
      {...common}
      type={field.kind === 'string' ? 'text' : 'number'}
      slotProps={{
        htmlInput: {
          ...(field.minimum === undefined ? {} : { min: field.minimum }),
          ...(field.maximum === undefined ? {} : { max: field.maximum }),
          ...(field.pattern === undefined ? {} : { pattern: field.pattern }),
        },
      }}
    />
  );
}
